import { BaseApi } from '../base/BaseApi';
import { type IMediaApi } from './IMediaApi';
import { MediaDownloadRequestDTO, MediaDownloadResponseDTO } from 'infrastructure/dtos/media';
import { AxiosClient } from 'infrastructure/axiosClient/AxiosClient';
import axios, { AxiosInstance } from 'axios';

export class MediaApi extends BaseApi implements IMediaApi {
  private readonly tempFiles: Map<string, { blob: Blob; url: string }> = new Map();
  private readonly externalAxios: AxiosInstance; // Axios instance for external requests without auth headers

  constructor(axiosClient: AxiosClient) {
    super(axiosClient);
    // Create a separate axios instance for external requests without authorization headers
    this.externalAxios = axios.create({
      timeout: 30000, // 30 second timeout
      headers: {
        'User-Agent':
          'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36',
      },
    });
  }

  private getFilenameFromUrl(url: string, filename?: string): string {
    const urlPath = new URL(url).pathname;
    const urlFilename = urlPath.split('/').pop() || '';
    return filename || urlFilename || `download_${Date.now()}`;
  }

  private getExtensionFromMimeType(mimeType: string): string {
    const mimeToExtension: Record<string, string> = {
      'image/jpeg': '.jpg',
      'image/jpg': '.jpg',
      'image/png': '.png',
      'image/gif': '.gif',
      'image/webp': '.webp',
      'image/svg+xml': '.svg',
      'video/mp4': '.mp4',
      'video/webm': '.webm',
      'video/ogg': '.ogv',
      'video/avi': '.avi',
      'video/mov': '.mov',
      'video/wmv': '.wmv',
      'video/flv': '.flv',
    };

    return mimeToExtension[mimeType] || '';
  }

  private async downloadFile(url: string, filename?: string): Promise<MediaDownloadResponseDTO> {
    try {
      // Use external axios instance without authorization headers for external URLs
      const response = await this.externalAxios.get(url, { responseType: 'arraybuffer' });

      // Determine filename and extension
      const finalFilename = this.getFilenameFromUrl(url, filename);
      const mimeType = response.headers['content-type'] || 'application/octet-stream';
      const extension = this.getExtensionFromMimeType(mimeType);
      const filenameWithExtension = finalFilename.includes('.')
        ? finalFilename
        : `${finalFilename}${extension}`;

      // Create blob from response data
      const blob = new Blob([response.data], { type: mimeType });
      const blobUrl = URL.createObjectURL(blob);

      // Store reference for cleanup
      this.tempFiles.set(blobUrl, { blob, url: blobUrl });

      return {
        filePath: blobUrl, // In browser, this is the blob URL
        originalUrl: url,
        filename: filenameWithExtension,
        fileSize: response.data.byteLength,
        mimeType,
        downloadDate: new Date().toISOString(),
      };
    } catch (error: unknown) {
      if (error instanceof Error) {
        if (error.message.includes('CORS')) {
          throw new Error(`CORS error: Cannot download file from ${url}.`);
        }
      }
      throw new Error(`Failed to download file from ${url}: ${error}`);
    }
  }

  public async downloadImage(dto: MediaDownloadRequestDTO): Promise<MediaDownloadResponseDTO> {
    const result = await this.downloadFile(dto.url, dto.filename);

    // Validate that it's actually an image
    if (!result.mimeType.startsWith('image/')) {
      await this.cleanupTempFile(result.filePath);
      throw new Error('The downloaded file is not an image');
    }

    return result;
  }

  public async downloadVideo(dto: MediaDownloadRequestDTO): Promise<MediaDownloadResponseDTO> {
    const result = await this.downloadFile(dto.url, dto.filename);

    // Validate that it's actually a video
    if (!result.mimeType.startsWith('video/')) {
      await this.cleanupTempFile(result.filePath);
      throw new Error('The downloaded file is not a video');
    }

    return result;
  }

  public async downloadMedia(dto: MediaDownloadRequestDTO): Promise<MediaDownloadResponseDTO> {
    return await this.downloadFile(dto.url, dto.filename);
  }

  public async cleanupTempFile(filePath: string): Promise<void> {
    try {
      const fileRef = this.tempFiles.get(filePath);
      if (fileRef) {
        URL.revokeObjectURL(fileRef.url);
        this.tempFiles.delete(filePath);
      }
    } catch (error) {
      console.warn(`Failed to cleanup temp file ${filePath}:`, error);
    }
  }

  // Additional method to trigger browser download
  public async downloadToUserDownloads(filePath: string, filename: string): Promise<void> {
    const fileRef = this.tempFiles.get(filePath);
    if (!fileRef) {
      throw new Error('File not found in temporary storage');
    }

    const link = document.createElement('a');
    link.href = fileRef.url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
